import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { AllergyService } from 'src/app/services/allergy.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NzSelectModule, NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { IAllergyDropdown } from 'src/app/interfaces/Dropdown/IAllergyDropdown';
import { CommonModule } from '@angular/common';
import { IAddAllergy } from 'src/app/interfaces/Add/IAddAllergy';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { IReactionDetailed } from 'src/app/interfaces/Detailed/IReactionDetailed';
import { v4 as uuidv4 } from 'uuid';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { TooltipComponent } from '../../../../../../../common/tooltip/tooltip.component';
import { NzIconModule } from 'ng-zorro-antd/icon';
import Swal from 'sweetalert2';
import { Subject, takeUntil } from 'rxjs';
import { NzNoAnimationModule } from 'ng-zorro-antd/core/no-animation';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';
import {
  dateRangeValidator,
  dynamicDateRangeValidator,
} from 'src/date/date.configuration';

@Component({
    selector: 'app-allergies-add-edit',
    templateUrl: './allergies-add-edit.component.html',
    styleUrls: ['./allergies-add-edit.component.css'],
    imports: [
        NzModalModule,
        TranslateModule,
        FormsModule,
        FlexLayoutModule,
        ReactiveFormsModule,
        NzSelectModule,
        CommonModule,
        NzButtonModule,
        NzTableModule,
        NzLayoutModule,
        TooltipComponent,
        NzIconModule,
        NzNoAnimationModule,
    ]
})
export class AllergiesAddEditComponent {
  private destroy$ = new Subject<void>();

  reactionsAddedDetailed: IReactionDetailed[] = [];

  allergyDropdown: IAllergyDropdown | undefined;

  substancesProducts: NzSelectOptionInterface[] = [];

  isVisible = false;
  isLoading = false;

  isEdit = false;
  editAllergyId?: number;

  reactionsSubmitted = false;
  allergySubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  allergyForm: FormGroup = new FormGroup({});

  reactionsForm: FormGroup = new FormGroup({});

  constructor(
    private readonly allergyService: AllergyService,
    private readonly formBuilder: FormBuilder,
    private translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.isEdit = false;

    this.allergyService.getAllergyDropdown().subscribe({
      next: (pro) => {
        this.allergyDropdown = pro?.data;

        this.substancesProducts = this.allergyDropdown?.substanceProduct.map(
          (d) => {
            return {
              label: d?.name,
              value: d?.id,
            };
          }
        );

        this.isLoading = false;
      },
      error: (err) => {},
    });

    this.allergyForm = this.formBuilder.group({
      allergyValue: [null, Validators.required],
      typeValue: [null, Validators.required],
      criticalityValue: [null, Validators.required],
      statusValue: [null, Validators.required],
      resolutionDate: [null],
      description: [null, Validators.required],
    });

    this.reactionsForm = this.formBuilder.group({
      manifestationValue: [null, Validators.required],
      description: [null, Validators.required],
      onsetDate: [
        null,
        [
          Validators.required,
          dynamicDateRangeValidator(
            undefined,
            () =>
              this.allergyForm?.get('resolutionDate')?.value ||
              this.reactionsForm?.get('endDate')?.value ||
              this.getCurrentDate()
          ),
        ],
      ],
      endDate: [
        null,
        [
          dynamicDateRangeValidator(
            () => this?.reactionsForm?.value?.onsetDate,
            () =>
              this?.allergyForm?.value?.resolutionDate || this.getCurrentDate()
          ),
        ],
      ],
      severityValue: [null, Validators.required],
      certaintyValue: [null, Validators.required],
    });

    this.allergyService.allergyEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.editAllergyId = data?.id;

          this.allergyForm.patchValue({
            allergyValue: data?.allergyId,
            typeValue: data?.eventTypeId,
            criticalityValue: data?.criticalityId,
            statusValue: data?.allergyStatusId,
            resolutionDate: data?.resolutionDate,
            description: data?.description,
          });

          this.reactionsAddedDetailed = [...data?.reactionDto!];
        } else {
          this.isEdit = false;
          this.editAllergyId = undefined;

          this.reactionsAddedDetailed = [];

          this?.allergyForm.reset();
          this?.reactionsForm.reset();
        }
      });

    this.allergyService.allergyModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    // Unsubscribe from observables
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  public allergyName?: string;

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  handleCancel() {
    this.reactionsAddedDetailed = [];

    this.allergyForm.reset();
    this.reactionsForm.reset();

    this.allergySubmitted = false;
    this.reactionsSubmitted = false;

    this.isLoading = false;

    this.allergyService.allergyModalSub.next(false);

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.allergySubmitted = true;

    if (this.allergyForm.valid) {
      const allergyObj: IAddAllergy = {};

      allergyObj.id = this.editAllergyId;
      allergyObj.allergyCategoryId = this.findCategoryId()?.toString();
      allergyObj.allergyId = this.allergyForm.value?.allergyValue;
      allergyObj.eventTypeId = this.allergyForm.value?.typeValue;
      allergyObj.criticalityId = this.allergyForm.value?.criticalityValue;
      allergyObj.allergyStatusId = this.allergyForm.value?.statusValue;
      allergyObj.resolutionDate = this.allergyForm.value?.resolutionDate;
      allergyObj.description = this.allergyForm.value?.description;
      allergyObj.reactionDto = this.reactionsAddedDetailed;

      if (this.isEdit) {
        this.allergyService.editAllergy(allergyObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess('Allergy edited successfully');
            this.allergyService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to edit allergy');
            this.handleCancel();
          },
        });
      } else {
        this.allergyService.addAllergy(allergyObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess('Allergy added successfully');
            this.allergyService.getData();

            this.handleCancel();
          },

          error: (err) => {
            this.swalToastService.toastError('Unable to add allergy');
            this.handleCancel();
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }

  findCategory() {
    const categoryId = this?.allergyDropdown?.substanceProduct?.find(
      (c) => c.id == this?.allergyForm.value.allergyValue
    )?.allergyCategoryId;

    return (
      this?.allergyDropdown?.category?.find((c) => c.id == categoryId)?.name ||
      this.translate.instant('Category based on Substance/Product')
    );
  }

  findCategoryId() {
    const categoryId = this?.allergyDropdown?.substanceProduct?.find(
      (c) => c.id == this?.allergyForm.value.allergyValue
    )?.allergyCategoryId;

    return this?.allergyDropdown?.category?.find((c) => c.id == categoryId)?.id;
  }

  addReaction() {
    this.reactionsSubmitted = true;

    if (this.reactionsForm.valid) {
      const reactionObj: IReactionDetailed = {};

      reactionObj.reactionId = uuidv4();

      reactionObj.manifestationId =
        this.reactionsForm.value?.manifestationValue;
      reactionObj.onSetDate = this.reactionsForm.value?.onsetDate;
      reactionObj.endDate = this.reactionsForm.value?.endDate;
      reactionObj.severityId = this.reactionsForm.value?.severityValue;
      reactionObj.certaintyId = this.reactionsForm.value?.certaintyValue;
      reactionObj.description = this.reactionsForm.value?.description;
      reactionObj.manifestation = this.allergyDropdown?.manifestation.find(
        (m) =>
          m.id.toString() ==
          this.reactionsForm.value?.manifestationValue.toString()
      )?.name;
      reactionObj.severity = this.allergyDropdown?.severity.find(
        (s) =>
          s.id.toString() == this.reactionsForm.value?.severityValue.toString()
      )?.name;
      reactionObj.certainty = this.allergyDropdown?.certainty.find(
        (c) =>
          c.id.toString() == this.reactionsForm.value?.certaintyValue.toString()
      )?.name;
      if (this.reactionsForm.value?.onsetDate == '') {
        reactionObj.onSetDate = undefined;
      }
      if (this.reactionsForm.value?.endDate == '') {
        reactionObj.endDate = undefined;
      }

      this.reactionsAddedDetailed.push(reactionObj);

      this.reactionsForm.reset();

      this.reactionsSubmitted = false;

      this.cdr.detectChanges();
    }
  }

  removeReaction(reactionId: string) {
    this.reactionsAddedDetailed = this.reactionsAddedDetailed.filter(
      (r) => r.reactionId != reactionId && r.id?.toString() != reactionId
    );
  }

  getMinDate(): string | null {
    const maxDate = this.reactionsAddedDetailed?.length
      ? this.reactionsAddedDetailed.reduce(
          (maxDate: string | null, reaction) => {
            const currentDate = reaction.endDate
              ? new Date(reaction.endDate).toISOString()
              : null;
            if (!maxDate || (currentDate && currentDate > maxDate)) {
              return currentDate;
            }
            return maxDate;
          },
          null // Initial value is null
        )
      : null;

    return maxDate?.split('T')?.at(0) || null;
  }
}
